import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { AuthTokenService } from '@helios/auth'
import { BasePanelComponent, LayoutContainer } from '@helios/panel-base'
import { ComponentContainer } from 'golden-layout'
import { fromEvent, Subscription } from 'rxjs'
import { take } from 'rxjs/operators'
// Side-effect import: pulls in the `declare global` augmentation that types
// `window.copilot`. Importing a real symbol keeps the linter happy.
import '@helios/copilot'

@Component({
  selector: 'hs-clarity-ops',
  templateUrl: './clarity-ops-panel.component.html',
  styleUrls: ['./clarity-ops-panel.component.scss'],
})
export class ClarityOpsPanelComponent
  extends BasePanelComponent
  implements OnInit, OnDestroy
{
  source = 'helios'

  /**
   * The CIAM access token handed to the embedded
   * `<clarity-ops-visualizer-ui>` web component, which attaches it as a
   * bearer header on its two direct calls to the clarity-ops data API.
   *
   * Resolved EXACTLY ONCE in `ngOnInit` and bound through the `async` pipe —
   * deliberately no timer, interval or refresh poll. `getToken()` delegates to
   * `getAccessTokenSilentlyOrLogout()`, which on an unrenewable SSO session
   * performs a logout redirect; Helios is a SPA, so that redirect is app-wide,
   * not panel-scoped. A background poll in a panel people leave open all day
   * could therefore evict the user from all of Helios at a moment they did not
   * initiate. Recovery from an expired token is instead the web component's
   * distinguishable 401 message ("session expired, reload the page").
   *
   * Undefined until `ngOnInit` runs, and the `async` pipe emits `null` until
   * the promise settles — the web component sends no `Authorization` header
   * for either of those states.
   */
  authToken$?: Promise<string>

  private copilotInitSub?: Subscription

  constructor(
    @Inject(LayoutContainer) container: ComponentContainer,
    private readonly authTokenService: AuthTokenService,
  ) {
    super(container)
  }

  ngOnInit() {
    // Resolve once — see the `authToken$` doc comment for why there is no poll.
    this.authToken$ = this.authTokenService.getToken()

    if (window.copilot) {
      window.copilot.open()
      return
    }
    this.copilotInitSub = fromEvent(window, 'CopilotInitialized')
      .pipe(take(1))
      .subscribe(() => window.copilot?.open())
  }

  ngOnDestroy() {
    this.copilotInitSub?.unsubscribe()
  }
}
